/**
 * The filters a drill-down link carries, serialised once for every screen that builds one.
 *
 * A bare link opens the detail on its own default period, its own role and EVERY eligible community, so
 * whatever this fails to emit is filled in at the destination from the browser's stored scope - and the
 * screen then explains a different cohort than the card that was clicked, without saying so. Nothing
 * fails; the numbers are merely about someone else.
 *
 * Shared because the two link builders that existed had already diverged: one carried the role, the other
 * did not, and neither carried the communities. Read {@link reportLinkQuery} for the other half of the
 * trip, which is how the destination reads these back.
 */
export type ReportLinkFilters = {
  periodRange?: string;
  startDate: Date;
  endDate: Date;
  previousPeriodStartDate?: Date;
  previousPeriodEndDate?: Date;
  /**
   * O papel pedido como ESTREITAMENTO, e opcional desde a #360.
   *
   * Ausente é o modo "Tudo o que eu alcanço": o recorte sai do `scopeByProduct` que a `platform-api`
   * resolve, e é o único que fala de acesso por grant. Os DTOs da `report-api` validam este campo só
   * quando o mapa não vem.
   */
  userRoles?: string[];
  botConfigIds?: number[];
};

export const reportLinkParams = (filters: ReportLinkFilters) => {
  const params = new URLSearchParams();

  if (filters.botConfigIds?.length)
    params.set('communities', filters.botConfigIds.join(','));
  if (filters.periodRange) params.set('period', filters.periodRange);
  params.set('start', filters.startDate.toISOString());
  params.set('end', filters.endDate.toISOString());
  // The comparison window travels when the source has one, so a named range and a custom one land on the
  // same window the dashboard was showing rather than on a re-derived approximation of it.
  if (filters.previousPeriodStartDate)
    params.set('prevStart', filters.previousPeriodStartDate.toISOString());
  if (filters.previousPeriodEndDate)
    params.set('prevEnd', filters.previousPeriodEndDate.toISOString());
  /* Encadeamento opcional porque o campo é opcional: ausente é o modo sem papel, e ele não emite `role`. */
  if (filters.userRoles?.[0]) params.set('role', filters.userRoles[0]);

  return params;
};

/** A link to `path` carrying the scope the producer was looking at. */
export const reportLinkHref = (path: string, filters: ReportLinkFilters) =>
  `${path}?${reportLinkParams(filters).toString()}`;
